/**
 * SyncStep.tsx
 * Step 3: Phase 1 progress display.
 * Polls GET /api/ingestion/:jobId every 3 seconds.
 * Shows per-resource progress bars, flips to "you're live" when Phase 1 completes.
 */

import { useEffect, useState, useRef } from "react";
import { getJobStatus } from "../../lib/api";
import type { NetworkSummary, IngestionJob } from "../../lib/types";
import Spinner from "../../components/Spinner";

interface Props {
  network: NetworkSummary;
  jobId: string;
}

// Phase 1 targets — how many records we expect to sync (for progress bars)
// These are estimates; actuals come from the job status API
const PHASE1_TARGETS = {
  members:  { label: "Members",  icon: "👤", estimate: 100  },
  spaces:   { label: "Spaces",   icon: "🗂️", estimate: 50   },
  posts:    { label: "Posts",    icon: "📝", estimate: 500, note: "most recent" },
  events:   { label: "Events",   icon: "📅", estimate: 50   },
};

const POLL_INTERVAL_MS = 3000;

export default function SyncStep({ network, jobId }: Props) {
  const [job, setJob] = useState<IngestionJob | null>(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Start polling immediately
    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [jobId]);

  async function poll() {
    try {
      const result = await getJobStatus(jobId);
      setJob(result);
      if (result.phase1_status === "completed" || result.phase1_status === "failed") {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (result.phase1_status === "completed") {
          setTimeout(() => setDone(true), 600);
        }
      }
    } catch (err: any) {
      setError(err.message ?? "Failed to fetch sync status.");
    }
  }

  // ── Done state ─────────────────────────────────────────────────────────────
  if (done && job) {
    const backfillRemaining = (network.post_count ?? 0) - (job.posts_synced ?? 0);
    return (
      <div className="step-card">
        <div className="logo">
          <div className="logo-icon">⚡</div>
          <span className="logo-text">Hostology</span>
        </div>

        <div className="done-hero">
          <span className="done-emoji">🎉</span>
          <h1 className="step-title">You're live</h1>
          <p className="step-sub">
            <strong className="highlight">{network.title}</strong> is now mirrored.<br />
            Your widgets are active and your data is portable.
          </p>
        </div>

        <div className="done-stats">
          {Object.entries(PHASE1_TARGETS).map(([key, meta]) => {
            const count = (job as any)[`${key}_synced`] ?? 0;
            return (
              <div key={key} className="done-stat">
                <span className="stat-icon">{meta.icon}</span>
                <span className="done-stat-value">{count.toLocaleString()}</span>
                <span className="stat-label">{meta.label}</span>
              </div>
            );
          })}
        </div>

        {backfillRemaining > 0 && (
          <div className="info-box">
            <div className="info-box-title">🔄 Backfill running</div>
            <p className="info-box-body">
              ~{backfillRemaining.toLocaleString()} older posts are syncing slowly in the
              background. No action needed — they'll appear automatically.
            </p>
          </div>
        )}

        <a href="/dashboard" className="btn-primary" style={{ display: "block", textAlign: "center", textDecoration: "none" }}>
          Go to dashboard →
        </a>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (job?.phase1_status === "failed") {
    return (
      <div className="step-card">
        <div className="logo">
          <div className="logo-icon">⚡</div>
          <span className="logo-text">Hostology</span>
        </div>
        <h1 className="step-title">Sync failed</h1>
        <p className="step-sub">Something went wrong during the initial sync.</p>
        {job.last_error && <div className="error-banner">⚠️ {job.last_error}</div>}
        <button className="btn-primary" onClick={() => window.location.reload()}>
          Try again
        </button>
      </div>
    );
  }

  // ── In-progress state ──────────────────────────────────────────────────────
  return (
    <div className="step-card">
      <div className="logo">
        <div className="logo-icon">⚡</div>
        <span className="logo-text">Hostology</span>
      </div>

      <div className="step-title-row">
        <Spinner size={20} color="#3b82f6" />
        <h1 className="step-title">Syncing your community</h1>
      </div>
      <p className="step-sub">
        Mirroring your most recent data. This usually takes under a minute.
      </p>

      {error && <div className="error-banner">⚠️ {error} — retrying…</div>}

      <div className="progress-list">
        {Object.entries(PHASE1_TARGETS).map(([key, meta]) => {
          const synced = job ? (job as any)[`${key}_synced`] ?? 0 : 0;
          const target = meta.estimate;
          const pct = Math.min(100, Math.round((synced / target) * 100));
          const isDone = job?.phase1_status === "completed" || synced >= target;

          return (
            <div key={key} className="progress-row">
              <div className="progress-row-header">
                <div className="progress-row-label">
                  <span className="progress-icon">{meta.icon}</span>
                  <span className={isDone ? "progress-label-done" : "progress-label"}>
                    {meta.label}
                    {"note" in meta && meta.note && (
                      <span className="progress-note"> ({meta.note})</span>
                    )}
                  </span>
                </div>
                <span className={isDone ? "progress-count-done" : "progress-count"}>
                  {isDone ? "✓ Done" : job ? `${synced.toLocaleString()} / ~${target.toLocaleString()}` : "—"}
                </span>
              </div>
              <div className="progress-track">
                <div
                  className={isDone ? "progress-fill-done" : "progress-fill"}
                  style={{ width: `${isDone ? 100 : pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="info-box" style={{ marginTop: 24 }}>
        <p className="info-box-body">
          📦 Historical backfill will run quietly in the background after this completes.
        </p>
      </div>
    </div>
  );
}
